import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

import itcounsulting from "../img/itcounsulting.jpg";
import cloud from "../img/cloud.jpg";
import bps from "../img/bps.jpg";
import ai from "../img/ai.jpg";
import dm from "../img/dm.jpg";
import cyber from "../img/cyber.jpg";
import csd from "../img/csd.jpg";
import ui from "../img/ui.jpg";

const cardVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const Services = () => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const updateButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  useEffect(() => {
    updateButtons();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateButtons);
    return () => el.removeEventListener("scroll", updateButtons);
  }, []);

  const scrollLeftAction = () => {
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRightAction = () => {
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const services = [
    {
      title: "Custom Software Development",
      description:
        "We build secure, scalable, and innovative software solutions that drive real business growth and digital transformation.",
      mainImage: csd,
      link: "/custom-software-development",
    },
    {
      title: "IT Support & Managed Services",
      description:
        "We help businesses in Hyderabad run smoothly and grow efficiently with reliable, proactive IT support and managed services.",
      mainImage: itcounsulting,
      link: "/it-support-services",
    },
    {
      title: "Cloud & Infrastructure Services",
      description:
        "Trusted cloud service providers delivering secure, scalable, and cost-effective cloud solutions for modern business success.",
      mainImage: cloud,
      link: "/cloud-service-providers",
    },
    {
      title: "Business Process Outsourcing (BPO)",
      description:
        "Boost efficiency, reduce operational costs, and enhance customer satisfaction with our reliable and intelligent BPO solutions.",
      mainImage: bps,
      link: "/bpo-services",
    },
    {
      title: "AI & Data Analytics Solutions",
      description:
        "Unlock real-time insights, predictive intelligence, and smarter workflows with advanced AI-powered data analytics.",
      mainImage: ai,
      link: "/data-analytics-services",
    },
    {
      title: "Digital Marketing Services",
      description:
        "Transform your brand’s digital presence with AI-driven, data-backed strategies that deliver measurable growth and engagement.",
      mainImage: dm,
      link: "/digital-marketing-services",
    },
    {
      title: "UI/UX Design Services",
      description:
        "Get modern, intuitive, and conversion-focused websites & mobile app designs that delight users and elevate your brand.",
      mainImage: ui,
      link: "/ui-ux-design-services",
    },
    {
      title: "Cybersecurity Services",
      description:
        "Protect your business with advanced cybersecurity solutions, proactive threat detection, and complete data assurance.",
      mainImage: cyber,
      link: "/cybersecurity-services",
    },
  ];

  /** Duplicate the array for infinite loop scrolling */
  const loopServices = [...services, ...services];

  /** Auto-scrolling infinite loop effect */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let frame;
    const speed = 0.6; // Adjust scroll speed

    const autoScroll = () => {
      el.scrollLeft += speed;

      // Reset to start when half scrolled (seamless loop)
      if (el.scrollLeft >= el.scrollWidth / 2) {
        el.scrollLeft = 0;
      }

      frame = requestAnimationFrame(autoScroll);
    };

    frame = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Leading Software Development Company in Hyderabad | SecurXperts</title>
        <meta
          name="description"
          content="SecurXperts offers custom software development, IT support, cloud solutions, cybersecurity, UI/UX design, digital marketing, and training in Hyderabad."
        />
      </Helmet>

      <section className="py-16 md:py-20 bg-white text-gray-900 relative" id="services">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 md:mb-16 gap-8">
            <div className="w-full lg:w-3/4">
              <p className="text-sm font-semibold uppercase text-gray-500 mb-1">OUR SERVICES</p>
              <h2 className="text-4xl md:text-5xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
                Innovation That Drives Real Results
              </h2>
            </div>
            <Link
              to="/our-services"
              className="inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 shadow-md transition-all duration-300"
            >
              VIEW ALL SERVICES →
            </Link>
          </div>

          {/* Scroll Buttons */}
          {showLeft && (
            <button
              onClick={scrollLeftAction}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-lg p-3 rounded-full z-20 hover:bg-gray-100"
            >
              ◀
            </button>
          )}

          {showRight && (
            <button
              onClick={scrollRightAction}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-lg p-3 rounded-full z-20 hover:bg-gray-100"
            >
              ▶
            </button>
          )}

          {/* Horizontal Scroll */}
          <motion.div
            ref={scrollRef}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.1 }}
            transition={{ staggerChildren: 0.1 }}
            className="flex flex-nowrap overflow-x-auto gap-8 pb-4 horizontal-scroll-container
                      [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {loopServices.map((service, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                className="flex-shrink-0 w-72 md:w-80 lg:w-72"
              >
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 pb-6 flex flex-col h-full 
                                transition-all duration-500 ease-out group transform hover:-translate-y-1 cursor-pointer
                                shadow-md hover:shadow-2xl hover:shadow-blue-500/50">

                  <div className="relative w-full h-40 rounded-t-xl overflow-hidden">
                    <img
                      src={service.mainImage}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>

                  <div className="p-4 pt-16 flex flex-col items-center text-center flex-grow">
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h4>
                    <p className="text-gray-600 text-base mb-4 flex-grow px-2">
                      {service.description}
                    </p>

                    <Link
                      to={service.link}
                      className="inline-flex items-center text-blue-600 font-semibold text-sm hover:text-blue-800"
                    >
                      READ MORE →
                    </Link>
                  </div>

                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
